import { useState, useCallback } from "react";
import { useToast } from "@repo/ui";
import { api } from "@/lib/api/client";
import { logger } from "@/lib/utils/logger";

export interface NDCLookupResult {
  item_name: string;
  brand_name?: string;
  generic_name?: string;
  drug_type?: "schedule_ii" | "schedule_iii" | "schedule_iv" | "schedule_v";
  dea_schedule?: string;
  dosage_form?: string;
  labeler_name?: string;
  packaging?: string[];
  marketing_category?: string;
  marketing_status?: string;
  nda?: string;
  anda?: string;
  bla?: string;
  color?: string;
  color_text?: string;
  shape?: string;
  shape_text?: string;
  imprint_code?: string;
  size?: string;
  score?: string;
  cached?: boolean;
  brand_name_customized?: boolean;
  generic_name_customized?: boolean;
  /** The resolved 5-4-2 formatted NDC (useful when a 10-digit input was expanded) */
  resolvedNDC?: string;
}

interface UseNDCLookupOptions {
  onSuccess?: (data: NDCLookupResult) => void;
  onError?: (error: string) => void;
  showToast?: boolean;
}

export function useNDCLookup(options: UseNDCLookupOptions = {}) {
  const { onSuccess, onError, showToast = true } = options;
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const lookupNDC = useCallback(
    async (ndc: string): Promise<NDCLookupResult | null> => {
      // Accept 10 or 11 digits (10-digit NDCs will be resolved server-side)
      const ndcDigits = ndc.replace(/\D/g, "");
      if (ndcDigits.length !== 10 && ndcDigits.length !== 11) {
        return null;
      }

      setIsLoading(true);

      try {
        const result = await api.get<{
          success?: boolean;
          data?: NDCLookupResult;
          resolvedNDC?: string;
          error?: string;
        }>("/ndc/lookup", { params: { ndc: ndcDigits } });

        if (result.success && result.data) {
          const drugData: NDCLookupResult = {
            ...result.data,
            // Propagate the resolved NDC from the API (5-4-2 formatted)
            resolvedNDC: result.resolvedNDC,
          };

          if (showToast) {
            toast({
              title: "Drug Information Found",
              description: `Auto-filled: ${drugData.brand_name || drugData.item_name}`,
              variant: "info",
            });
          }

          onSuccess?.(drugData);
          return drugData;
        }

        return null;
      } catch (error) {
        // Don't show error toast for 404 - user might be entering manually
        const statusCode = (error as { statusCode?: number }).statusCode;
        if (statusCode === 404) {
          onError?.("Drug not found in database");
          return null;
        }

        logger.error("NDC lookup failed", error, { operation: "ndc_lookup" });

        const errorMessage =
          (error as { message?: string }).message ||
          "Network error. Please try again.";

        if (showToast) {
          toast({
            title: "NDC Lookup Failed",
            description: errorMessage,
            variant: "destructive",
          });
        }

        onError?.(errorMessage);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess, onError, showToast, toast]
  );

  return { lookupNDC, isLoading };
}
